import { GaEvent } from '@support/types/google-analytics-events';
import { MakePropsRequired } from '@support/types/utility/make-props-required';

import { useCallback } from 'react';

import { SurveyType, useBookSurveyMutation } from '@graphql/requests';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { ContactDetails } from '@ui-furniture/common/contact-details-form';
import { useToast } from '@ui-furniture/common/toast';
import { useModalControls } from '@ui-furniture/lib/modal/context/modal-controls';

import { useSurveyRecommendation } from '../context/survey-recommendation';
import { AccessDetails } from '../ui/access-form';

export const useBookSurvey = ({
  surveyType,
  quotedPrice,
}: {
  surveyType: SurveyType;
  quotedPrice?: number;
}) => {
  const { reportObjective, sendTo } = useAnalyticsReportingService();
  const { recommendation } = useSurveyRecommendation();
  const [bookSurvey] = useBookSurveyMutation();
  const modal = useModalControls();
  const { showToast } = useToast();
  return useCallback(
    async ({
      contactDetails,
      access,
    }: {
      contactDetails: MakePropsRequired<ContactDetails, 'phone'>;
      access: AccessDetails;
    }) => {
      if (!recommendation)
        throw new Error(`Can't book a survey without a recommendation`);

      const {
        propertyType,
        specialistConstruction,
        address,
        ...propertyDetails
      } = recommendation.input;

      await bookSurvey({
        variables: {
          data: {
            surveyType,
            quotedPrice,
            contactDetails,
            ...(access.isSameAsContactDetails
              ? {
                  accessName: `${contactDetails.firstName} ${contactDetails.lastName}`,
                  accessPhoneNumber: contactDetails.phone,
                }
              : {
                  accessName: access.accessName,
                  accessPhoneNumber: access.accessPhoneNumber,
                }),
            propertyDetails: {
              ...propertyDetails,
              addressId: address.id,
              type: propertyType.value,
              specialistConstruction: specialistConstruction.map(
                (item) => item.value
              ),
            },
          },
        },
      });

      modal.close();

      showToast({
        message: 'Your survey has been booked. Someone will be in touch soon.',
        severity: 'success',
      });

      reportObjective({
        category: 'Survey',
        stage: 'complete',
        isConversion: true,
        name: 'Survey - Booked',
        data: {
          surveyType,
          quotedPrice,
          propertyPrice: propertyDetails.price,
          propertyValue: propertyType.value,
          propertyAddressId: address.id,
          propertyAddressLine1: address.line1,
          propertyAddressCity: address.city,
          propertyAddressPostcode: address.postcode,
          propertyBedroomCount: propertyDetails.bedroomCount,
          propertyAgeRange: propertyDetails.ageRange,
          propertyCondition: propertyDetails.condition,
          propertyDefects: propertyDetails.defects,
          propertyRenovationPlans: propertyDetails.renovationPlans,
          propertySpecialistConstruction: specialistConstruction.join(','),
        },
      });
      const gaData: GaEvent = {
        type: 'set',
        name: 'user_data',
        data: {
          email: contactDetails.email,
          phone_number: contactDetails.phone,
          address: {
            first_name: contactDetails.firstName,
            last_name: contactDetails.lastName,
            street: address.line1,
            city: address.city,
            postal_code: address.postcode,
            country: 'GB',
          },
        },
      };
      sendTo('ga', gaData);
    },
    []
  );
};
